import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewChild,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  input,
} from "@angular/core";
import { Marked } from "marked";
import hljs from "highlight.js";
import * as katex from "katex";
import { completePartialMarkdown } from "@copilotkit/core";

/**
 * Renders markdown content with syntax highlighting (highlight.js) and math (KaTeX).
 * Provides copy-to-clipboard on code blocks.
 * Uses ViewEncapsulation.None to style innerHTML — styles scoped to app-markdown-renderer.
 */
@Component({
  selector: "app-markdown-renderer",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [],
  templateUrl: "./markdown-renderer.component.html",
  styleUrl: "./markdown-renderer.component.scss",
})
export class MarkdownRendererComponent implements AfterViewInit {
  readonly content = input<string>("");

  @ViewChild("container", { static: false })
  private containerRef?: ElementRef<HTMLDivElement>;

  private readonly elementRef = inject(ElementRef);

  private markedInstance: Marked | null = null;

  private codeBlocksMap = new Map<string, string>();

  readonly renderedHtml = computed(() => {
    const completed = completePartialMarkdown(this.content());
    return this.renderMarkdown(completed);
  });

  constructor() {
    effect(() => {
      this.content();
      this.codeBlocksMap.clear();
      if (this.containerRef) {
        this.containerRef.nativeElement.innerHTML = this.renderedHtml();
        this.renderMathEquations();
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.containerRef) {
      this.containerRef.nativeElement.innerHTML = this.renderedHtml();
      this.renderMathEquations();
    }
  }

  protected handleClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const button = target.closest(".md-code-copy-btn") as HTMLButtonElement | null;
    if (!button) return;
    event.preventDefault();
    const blockId = button.getAttribute("data-block-id");
    if (!blockId) return;
    const code = this.codeBlocksMap.get(blockId);
    if (!code) return;

    navigator.clipboard.writeText(code).then(
      () => {
        const original = button.innerHTML;
        button.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg><span>Copied</span>`;
        setTimeout(() => {
          button.innerHTML = original;
        }, 2000);
      },
      (err) => console.error("MarkdownRendererComponent: clipboard write failed:", err),
    );
  }

  private renderMarkdown(content: string): string {
    this.initMarked();
    this.codeBlocksMap.clear();
    let html = this.markedInstance!.parse(content) as string;
    html = this.processMath(html);
    return html;
  }

  private initMarked(): void {
    if (this.markedInstance) return;
    this.markedInstance = new Marked();
    this.markedInstance.setOptions({ gfm: true, breaks: true });
    this.markedInstance.use({
      walkTokens: (token: any) => {
        if (token.type !== "code") return;
        const raw = token.text;
        const lang = token.lang ?? "";
        const blockId = this.hashId(raw);
        this.codeBlocksMap.set(blockId, raw);
        const language = hljs.getLanguage(lang) ? lang : "plaintext";
        const highlighted = hljs.highlight(raw, { language }).value;
        const codeClass = lang ? `hljs language-${lang}` : "hljs";
        token.type = "html";
        token.text = `
          <div class="md-code-block">
            <div class="md-code-header d-flex align-items-center justify-content-between">
              ${lang ? `<span class="md-code-lang">${lang}</span>` : "<span></span>"}
              <button class="md-code-copy-btn d-flex align-items-center gap-1" data-block-id="${blockId}" aria-label="Copy code">
                <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.11 0-2-.9-2-2V4c0-1.11.89-2 2-2h10c1.11 0 2 .89 2 2"/></svg>
                <span>Copy</span>
              </button>
            </div>
            <pre><code class="${codeClass}">${highlighted}</code></pre>
          </div>`;
      },
    });
  }

  private processMath(html: string): string {
    const codeBlocks: string[] = [];
    const codePH = "___CODE___";
    html = html.replace(/<pre><code[\s\S]*?<\/code><\/pre>/g, (m) => {
      codeBlocks.push(m);
      return `${codePH}${codeBlocks.length - 1}___`;
    });

    const inlineCodes: string[] = [];
    const inlinePH = "___INLINE___";
    html = html.replace(/<code>[\s\S]*?<\/code>/g, (m) => {
      inlineCodes.push(m);
      return `${inlinePH}${inlineCodes.length - 1}___`;
    });

    html = html.replace(/\$\$([\s\S]*?)\$\$/g, (_m, eq) => {
      try {
        return katex.renderToString(eq, { displayMode: true, throwOnError: false });
      } catch {
        return _m;
      }
    });
    html = html.replace(/\$([^$]+)\$/g, (_m, eq) => {
      try {
        return katex.renderToString(eq, { displayMode: false, throwOnError: false });
      } catch {
        return _m;
      }
    });

    codeBlocks.forEach((b, i) => {
      html = html.replace(`${codePH}${i}___`, b);
    });
    inlineCodes.forEach((c, i) => {
      html = html.replace(`${inlinePH}${i}___`, c);
    });
    return html;
  }

  private renderMathEquations(): void {
    const container = this.containerRef?.nativeElement;
    if (!container) return;
    container.querySelectorAll<HTMLElement>(".math-placeholder").forEach((el) => {
      const eq = el.getAttribute("data-equation");
      const display = el.getAttribute("data-display") === "true";
      if (eq) {
        try {
          katex.render(eq, el, { displayMode: display, throwOnError: false });
        } catch (err) {
          console.error("MarkdownRendererComponent: math render error:", err);
        }
      }
    });
  }

  private hashId(code: string): string {
    let hash = 0;
    for (let i = 0; i < code.length; i++) {
      hash = ((hash << 5) - hash + code.charCodeAt(i)) & 0xffffffff;
    }
    return `md-block-${hash}`;
  }
}
